import React, { useEffect, useRef } from 'react'
import { Box, Paper, Typography } from '@mui/material'
import { ADSENSE_CLIENT, ADSENSE_TEST_MODE } from '../lib/config'

// Extend Window interface for AdSense
declare global {
    interface Window {
        adsbygoogle: any[]
    }
}

interface AdSenseProps {
    slot: string
    format?: 'auto' | 'rectangle' | 'vertical' | 'horizontal'
    responsive?: boolean
    style?: React.CSSProperties
    className?: string
    testMode?: boolean
}

const AdSense: React.FC<AdSenseProps> = ({
    slot,
    format = 'auto',
    responsive = true,
    style = {},
    className = '',
    testMode = ADSENSE_TEST_MODE
}) => {
    const adRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (testMode) return // Skip loading in test mode

        // Ensure global queue exists
        (window as any).adsbygoogle = (window as any).adsbygoogle || []

        // Load Google AdSense script if not already loaded
        const existing = document.querySelector('script[data-adsbygoogle]') as HTMLScriptElement | null
        if (!existing) {
            const script = document.createElement('script')
            script.async = true
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'
            script.setAttribute('data-adsbygoogle', 'true')
            script.setAttribute('data-ad-client', ADSENSE_CLIENT)
            document.head.appendChild(script)
            script.addEventListener('load', () => {
                try { ((window as any).adsbygoogle).push({}) } catch (e) { console.warn('AdSense push error:', e) }
            })
        } else {
            // Already loaded; push immediately
            try { ((window as any).adsbygoogle).push({}) } catch (e) { console.warn('AdSense push error:', e) }
        }
    }, [testMode])

    // Default styles for different ad formats
    const getDefaultStyle = () => {
        switch (format) {
            case 'rectangle':
                return { width: '300px', height: '250px' }
            case 'vertical':
                return { width: '160px', height: '600px' }
            case 'horizontal':
                return { width: '728px', height: '90px' }
            default:
                return { width: '100%', minHeight: '90px' }
        }
    }

    const adStyle = {
        ...getDefaultStyle(),
        ...style,
        display: 'block',
        textAlign: 'center' as const
    }

    if (testMode) {
        return (
            <Paper
                elevation={1}
                sx={(theme) => ({
                    ...adStyle,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.100',
                    border: '2px dashed',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'grey.300',
                    color: theme.palette.text.secondary
                })}
                className={className}
            >
                <Box textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                        Google AdSense
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Slot: {slot}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Format: {format}
                    </Typography>
                </Box>
            </Paper>
        )
    }

    return (
        <Box
            ref={adRef}
            className={`adsense-container ${className}`}
            sx={{ ...adStyle }}
        >
            <ins
                className="adsbygoogle"
                style={{ display: 'block' }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={slot}
                data-ad-format={format}
                data-full-width-responsive={responsive ? 'true' : 'false'}
            />
        </Box>
    )
}

// Predefined ad components for common placements
export const HeaderAd: React.FC = () => (
    <AdSense
        slot="1234567890"
        format="horizontal"
        style={{ width: '100%', maxWidth: '728px', margin: '0 auto' }}
        testMode={true} // Set to false in production
    />
)

export const SidebarAd: React.FC = () => (
    <AdSense
        slot="1234567891"
        format="vertical"
        style={{ width: '160px', height: '600px' }}
        testMode={true} // Set to false in production
    />
)

export const InContentAd: React.FC = () => (
    <AdSense
        slot="1234567892"
        format="rectangle"
        style={{ width: '300px', height: '250px', margin: '20px auto' }}
        testMode={true} // Set to false in production
    />
)

export const FooterAd: React.FC = () => (
    <AdSense
        slot="1234567893"
        format="horizontal"
        style={{ width: '100%', maxWidth: '728px', margin: '20px auto' }}
        testMode={true} // Set to false in production
    />
)

export const MobileBannerAd: React.FC = () => (
    <AdSense
        slot="1234567894"
        format="auto"
        responsive={true}
        style={{ width: '100%', minHeight: '50px' }}
        testMode={true} // Set to false in production
    />
)

export default AdSense
